import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { SbNotification } from "uiCore";
import { getAgentPRDVersions, getPAICAgentJD, getPAICAgentPRD, saveAgentPRD } from "Api/apiService";
import useAuthStore from "Store/useAuthStore";
import usePAICStore from "Store/usePAICStore";
import analytics from "Utils/analytics";
import sanitizeMarkdown from "Utils/sanitizeMarkdown";
import { PAIC_VIEW_MODES, handlePAICErrorCode, PAIC_ERROR_MESSAGES } from "constants/peoplePlusAI.constants";

/**
 * This removes markdown inline-code backticks only for simple bullet list items.
 * Example:
 * * `Data Analysis`  ->  * Data Analysis
 * - `User Research`  ->  - User Research
 * It keeps real paragraph/code formatting untouched.
 */


/**
 * Small date formatter for toolbar meta.
 * If date is missing or invalid, returns empty string safely.
 */
const formatLastUpdatedText = (timestamp) => {
  if (!timestamp) return "";

  const rawDate =
    timestamp?.$date ||
    timestamp?.date ||
    timestamp;

  const parsedDate = new Date(rawDate);

  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }

  const dateStr = parsedDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const timeStr = parsedDate.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return `${dateStr}, ${timeStr}`;
};


const usePACJdPrdViewV2 = ({ defaultType }) => {
  const email = useAuthStore((s) => s.email);

  const hasInitializedSelectedAgent = useRef(false);
  const hasAppliedDefaultType = useRef(false);
  const previousDocumentTypeRef = useRef(null);

  const documentType = usePAICStore((s) => s.documentType);
  const viewMode = usePAICStore((s) => s.viewMode);
  const selectedDocumentAgentId = usePAICStore((s) => s.selectedDocumentAgentId);
  const selectedDocumentVersion = usePAICStore((s) => s.selectedDocumentVersion);
  const jdPrdLoading = usePAICStore((s) => s.jdPrdLoading);

  const selectedVersionWithAgents = usePAICStore((s) => s.selectedVersionWithAgents);
  const selectedVersionPrdData = usePAICStore((s) => s.selectedVersionPrdData);
  const selectedVersionJdData = usePAICStore((s) => s.selectedVersionJdData);

  const setDocumentType = usePAICStore((s) => s.setDocumentType);
  const setViewMode = usePAICStore((s) => s.setViewMode);
  const setSelectedDocumentAgentId = usePAICStore((s) => s.setSelectedDocumentAgentId);
  const setSelectedDocumentVersion = usePAICStore((s) => s.setSelectedDocumentVersion);
  const setJdPrdLoading = usePAICStore((s) => s.setJdPrdLoading);

  const setSelectedVersionPrdData = usePAICStore((s) => s.setSelectedVersionPrdData);
  const setSelectedVersionJdData = usePAICStore((s) => s.setSelectedVersionJdData);
  const clearSelectedVersionPrdData = usePAICStore((s) => s.clearSelectedVersionPrdData);
  const clearSelectedVersionJdData = usePAICStore((s) => s.clearSelectedVersionJdData);

  // PRD version history (agent-prd only): loaded per selected agent, newest first.
  const [prdVersions, setPrdVersions] = useState([]);
  const [selectedPrdVersion, setSelectedPrdVersion] = useState(null);
  const [savingPrd, setSavingPrd] = useState(false);

  useEffect(() => {
    if (hasAppliedDefaultType.current) return;

    if (defaultType === "agent-jd" || defaultType === "agent-prd") {
      setDocumentType(defaultType);
    }

    hasAppliedDefaultType.current = true;
  }, [defaultType, setDocumentType]);

  useEffect(() => {
    let isMounted = true;

    const fetchDocuments = async () => {
      const agentIds = selectedVersionWithAgents?.agent_unique_ids || [];

      analytics.trackPAICJDPRDViewOpened({
        from: "pac_jd_prd_view",
        email,
        documentType: defaultType,
        description: `Opened JD/PRD view for: ${defaultType}`,
      });

      // Do not clear maps here when persisted state is still rehydrating.
      if (!agentIds.length) {
        return;
      }

      setJdPrdLoading(true);

      try {
        const [prdResponses, jdResponses] = await Promise.all([
          Promise.all(agentIds.map((id) => getPAICAgentPRD({ id }))),
          Promise.all(agentIds.map((id) => getPAICAgentJD({ id }))),
        ]);

        if (!isMounted) return;

        const prdMap = {};
        const jdMap = {};

        agentIds.forEach((id, index) => {
          const prdItem = prdResponses[index]?.[0];
          const jdItem = jdResponses[index]?.[0];

          if (prdItem?.sb_agent_unique_id === id) {
            prdMap[id] = prdItem;
          }

          if (jdItem?.sb_agent_unique_id === id) {
            jdMap[id] = jdItem;
          }
        });

        setSelectedVersionPrdData(prdMap);
        setSelectedVersionJdData(jdMap);
      } catch (error) {
        if (!handlePAICErrorCode(error, SbNotification.error)) {
          SbNotification.error(PAIC_ERROR_MESSAGES.LOAD_AGENT_DOCUMENTS_FAILED);
        }
      } finally {
        if (isMounted) {
          setJdPrdLoading(false);
        }
      }
    };

    fetchDocuments();

    return () => {
      isMounted = false;
    };
  }, [
    defaultType,
    email,
    selectedVersionWithAgents,
    setJdPrdLoading,
    setSelectedVersionJdData,
    setSelectedVersionPrdData,
  ]);

  // A document always opens in view mode. viewMode is persisted, so reset it both on
  // open (in case a prior edit session was persisted) and on leave.
  useEffect(() => {
    setViewMode(PAIC_VIEW_MODES.VIEW);
    return () => {
      clearSelectedVersionPrdData();
      clearSelectedVersionJdData();
      setViewMode(PAIC_VIEW_MODES.VIEW);
    };
  }, [clearSelectedVersionJdData, clearSelectedVersionPrdData, setViewMode]);

  const aiAgentOptions = useMemo(() => {
    const source =
      documentType === "agent-prd"
        ? selectedVersionPrdData
        : selectedVersionJdData;

    return Object.values(source || {}).map((agent) => {
      const codeSuffix = agent?.sb_agent_code
        ? agent.sb_agent_code.split("_").pop()
        : "";

      return {
        value: agent.sb_agent_unique_id,
        label: `${codeSuffix} - ${agent.ai_agent_name}`,
      };
    });
  }, [documentType, selectedVersionJdData, selectedVersionPrdData]);

  useEffect(() => {
    if (previousDocumentTypeRef.current === null) {
      previousDocumentTypeRef.current = documentType;
      return;
    }

    if (previousDocumentTypeRef.current !== documentType) {
      previousDocumentTypeRef.current = documentType;
      hasInitializedSelectedAgent.current = false;
      setSelectedDocumentAgentId(null);
    }
  }, [documentType, setSelectedDocumentAgentId]);

  useEffect(() => {
    if (!aiAgentOptions.length) {
      if (selectedDocumentAgentId !== null) {
        setSelectedDocumentAgentId(null);
      }
      hasInitializedSelectedAgent.current = false;
      return;
    }

    const stillExists = aiAgentOptions.some(
      (option) => option.value === selectedDocumentAgentId,
    );

    if (!hasInitializedSelectedAgent.current && !selectedDocumentAgentId) {
      setSelectedDocumentAgentId(aiAgentOptions[0].value);
      hasInitializedSelectedAgent.current = true;
      return;
    }

    if (selectedDocumentAgentId && !stillExists) {
      setSelectedDocumentAgentId(aiAgentOptions[0].value);
    }
  }, [aiAgentOptions, selectedDocumentAgentId, setSelectedDocumentAgentId]);

  /**
   * Selected document object for the active type.
   * This is the single source for meta fields like owner, version, date, status.
   */
  // The specific PRD version row the user picked in the dropdown (agent-prd only).
  const selectedPrdVersionRow = useMemo(() => {
    if (documentType !== "agent-prd") return null;
    const chosen = prdVersions.find(
      (v) => String(v.version) === String(selectedPrdVersion),
    );
    return chosen && chosen.sb_agent_unique_id === selectedDocumentAgentId
      ? chosen
      : null;
  }, [documentType, prdVersions, selectedPrdVersion, selectedDocumentAgentId]);

  const selectedDocumentData = useMemo(() => {
    if (!selectedDocumentAgentId) return null;

    if (selectedPrdVersionRow) return selectedPrdVersionRow;

    return documentType === "agent-prd"
      ? selectedVersionPrdData?.[selectedDocumentAgentId] || null
      : selectedVersionJdData?.[selectedDocumentAgentId] || null;
  }, [
    documentType,
    selectedDocumentAgentId,
    selectedVersionJdData,
    selectedVersionPrdData,
    selectedPrdVersionRow,
  ]);

const markdownData = useMemo(() => {
  if (!selectedDocumentAgentId) return "";

  if (selectedPrdVersionRow) {
    return sanitizeMarkdown(selectedPrdVersionRow.agent_prd_md || "");
  }

  const selectedPrd = selectedVersionPrdData?.[selectedDocumentAgentId];
  const selectedJd = selectedVersionJdData?.[selectedDocumentAgentId];

  const raw = documentType === "agent-prd"
    ? selectedPrd?.agent_prd_md || ""
    : selectedJd?.agent_jd_md || "";

  return sanitizeMarkdown(raw);
}, [
  documentType,
  selectedDocumentAgentId,
  selectedVersionJdData,
  selectedVersionPrdData,
  selectedPrdVersionRow,
]);

// PRD version dropdown options + label (agent-prd only).
const prdVersionOptions = useMemo(
  () =>
    prdVersions.map((v) => ({
      label: `PRD Version - ${Number(v.version).toFixed(2)}`,
      value: String(v.version),
    })),
  [prdVersions],
);

const prdVersionLabel = selectedPrdVersion
  ? `PRD Version - ${Number(selectedPrdVersion).toFixed(2)}`
  : "PRD Version";

  /**
   * Toolbar meta fields
   */
  const versionLabel = useMemo(() => {
    if (!selectedDocumentData?.version) {
      return documentType === "agent-prd" ? "PRD Version" : "JD Version";
    }

    const labelPrefix = documentType === "agent-prd" ? "PRD Version" : "JD Version";
    return `${labelPrefix} - ${selectedDocumentData.version}`;
  }, [documentType, selectedDocumentData]);

  const modifiedText = useMemo(() => {
    const formattedDate = formatLastUpdatedText(selectedDocumentData?.last_updated_time_stamp);

    return formattedDate ? `Modified on ${formattedDate}` : "";
  }, [selectedDocumentData]);

  const ownerText = useMemo(() => {
    const ownerName = selectedDocumentData?.current_owner_full_name;

    return ownerName ? `Owner: ${ownerName}` : "";
  }, [selectedDocumentData]);

const documentStatus = useMemo(() => {
  if (documentType === "agent-prd") {
    return selectedDocumentData?.agent_prd_status || "";
  }

  if (documentType === "agent-jd") {
    return selectedDocumentData?.agent_jd_status || "";
  }

  return "";
}, [documentType, selectedDocumentData]);

  const handlePagination = useCallback(
    (direction) => {
      if (!aiAgentOptions.length) return;

      let index = aiAgentOptions.findIndex(
        (agent) => agent.value === selectedDocumentAgentId,
      );

      if (index < 0) {
        setSelectedDocumentAgentId(aiAgentOptions[0].value);
        return;
      }

      const nextIndex =
        direction === "PREV"
          ? index === 0
            ? aiAgentOptions.length - 1
            : index - 1
          : index === aiAgentOptions.length - 1
            ? 0
            : index + 1;

      const nextId = aiAgentOptions[nextIndex]?.value;

      if (nextId !== selectedDocumentAgentId) {
        setSelectedDocumentAgentId(nextId);
      }

      analytics.trackPAICJDPRDPagination({
        from: "pac_jd_prd_view",
        email,
        direction,
        description: `JD/PRD pagination: ${direction}`,
      });
    },
    [aiAgentOptions, email, selectedDocumentAgentId, setSelectedDocumentAgentId],
  );

  const enterEditMode = useCallback(() => {
    if (viewMode !== PAIC_VIEW_MODES.EDIT) {
      setViewMode(PAIC_VIEW_MODES.EDIT);
    }

    analytics.trackPAICJDPRDEditMode({
      from: "pac_jd_prd_view",
      email,
      documentType,
      description: "Entered edit mode for JD/PRD",
    });
  }, [documentType, email, setViewMode, viewMode]);

  const cancelEditMode = useCallback(() => {
    if (viewMode !== PAIC_VIEW_MODES.VIEW) {
      setViewMode(PAIC_VIEW_MODES.VIEW);
    }

    analytics.trackPAICJDPRDCancel({
      from: "pac_jd_prd_view",
      email,
      documentType,
      description: "Cancelled JD/PRD edit",
    });
  }, [documentType, email, setViewMode, viewMode]);

  // Load PRD version history for the selected agent (agent-prd only), newest first.
  useEffect(() => {
    if (documentType !== "agent-prd" || !selectedDocumentAgentId) {
      return undefined;
    }
    let active = true;
    (async () => {
      try {
        const data = await getAgentPRDVersions(selectedDocumentAgentId);
        if (!active) return;
        const list = Array.isArray(data) ? data : [];
        setPrdVersions(list);
        setSelectedPrdVersion(list.length ? String(list[0].version) : null);
      } catch (error) {
        if (active) {
          setPrdVersions([]);
          setSelectedPrdVersion(null);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [documentType, selectedDocumentAgentId]);

  // Save the edited markdown as a new PRD version, then refresh history to the new latest.
  const handleSavePrd = useCallback(
    async (editedMd) => {
      if (!selectedDocumentAgentId) return;
      if (!editedMd || !editedMd.trim()) {
        SbNotification.error("PRD cannot be empty");
        return;
      }
      setSavingPrd(true);
      try {
        await saveAgentPRD(selectedDocumentAgentId, editedMd);
        const data = await getAgentPRDVersions(selectedDocumentAgentId);
        const list = Array.isArray(data) ? data : [];
        setPrdVersions(list);
        setSelectedPrdVersion(list.length ? String(list[0].version) : null);
        setViewMode(PAIC_VIEW_MODES.VIEW);
        SbNotification.success("PRD version saved");
      } catch (error) {
        if (!handlePAICErrorCode(error, SbNotification.error)) {
          SbNotification.error("Failed to save PRD version");
        }
      } finally {
        setSavingPrd(false);
      }
    },
    [selectedDocumentAgentId, setViewMode],
  );

  return {
    documentType,
    viewMode,
    selectedDocumentAgentId,
    selectedDocumentVersion,
    jdPrdLoading,
    selectedVersionPrdData,
    selectedVersionJdData,
    selectedDocumentData,
    selectedVersionWithAgents,
    aiAgentOptions,
    markdownData,
    versionLabel,
    modifiedText,
    ownerText,
    documentStatus,
    setDocumentType,
    setViewMode,
    setSelectedDocumentAgentId,
    setSelectedDocumentVersion,
    handlePagination,
    enterEditMode,
    cancelEditMode,
    prdVersionOptions,
    prdVersionLabel,
    selectedPrdVersion,
    setSelectedPrdVersion,
    savingPrd,
    handleSavePrd,
  };
};

export default usePACJdPrdViewV2;